import { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Snackbar,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Background from "../../../components/Background";
import Loader from "../../../components/Loader";
import MenteeHeader from "../../../components/MenteeHeader";
import Mobilesidebar from "../../../components/Mobilesidebar";
import Sidebar from "../../../components/Sidebar";
import SuccessModal from "../../../components/SuccessModal";
import { baseFetch } from "../../../helpers/apiClient";
import { buildCanonicalProfileUpdate } from "../../../helpers/canonicalProfileUpdate";
import {
  EDIT_PROFILE_PAGE_PADDING,
  EDIT_PROFILE_PANEL_MAX_HEIGHT,
} from "../../../helpers/layoutConstants";
import {
  clearUpdatedFields,
  fetchProfileInfo,
  updateProfileData,
} from "../../../redux/profileSlice";
import { toggleSessionModal } from "../../../redux/sessionSlice";
import { mergeEditableProfile } from "./editableProfile";
import AdditionalPhotosEditor from "./AdditionalPhotosEditor";
import { BIOGRAPHY_MAX_LENGTH } from "./biography";
import EditProfileRightSection from "./EditProfileRightSection";
import GoalsSection from "./GoalsSection";
import V2MentorDetails from "./V2MentorDetails";
import V2ProfileBasics from "./V2ProfileBasics";
import V2ProfileDemographics from "./V2ProfileDemographics";
import { hasDuplicateEmployment } from "../../../helpers/employment";

const V2EditProfile = () => {
  const profileInfo = useSelector((state) => state.profileSlice.profile);
  const updatedFields = useSelector(
    (state) => state.profileSlice?.updatedFields ?? {},
  );
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [hasInvalidDob, setHasInvalidDob] = useState(false);
  // Bumped by Discard to remount the sections that copy props into state.
  const [formKey, setFormKey] = useState(0);
  const headingRef = useRef(null);
  // Keep a local display copy while the endpoint saves the whole photo set
  // immediately; the profile store is updated after a successful request too.
  const [additionalPhotos, setAdditionalPhotos] = useState(
    profileInfo?.profile?.photos?.map(({ url }) => url) ?? [],
  );

  const localProfile = useMemo(
    () => mergeEditableProfile(profileInfo, updatedFields),
    [profileInfo, updatedFields],
  );
  const isMentor = profileInfo?.roles?.includes("mentor");
  const hasUpdatedFields = Object.keys(updatedFields).length > 0;
  const isBiographyOverLimit =
    updatedFields?.biography?.length > BIOGRAPHY_MAX_LENGTH;
  const disable =
    loading || hasInvalidDob || isBiographyOverLimit || !hasUpdatedFields;
  const disableDiscard = loading || (!hasUpdatedFields && !hasInvalidDob);

  const handleDiscard = () => {
    dispatch(clearUpdatedFields());
    setHasInvalidDob(false);
    setFormKey((key) => key + 1);
    headingRef.current?.focus();
  };

  const handleAdditionalPhotosChange = (next) => {
    setAdditionalPhotos(next);
    dispatch(
      updateProfileData({
        photos: next.map((url, position) => ({ url, position: position + 1 })),
      }),
    );
  };

  const handleSaveChanges = async () => {
    if (hasDuplicateEmployment(updatedFields.employmentHistory)) {
      setErrorMessage(
        "Duplicate job title and employer combination found. Please remove duplicates before proceeding.",
      );
      setShowToast(true);
      return;
    }

    setLoading(true);
    setErrorMessage("");
    try {
      const response = await baseFetch({
        url: "/v2/me/profile",
        method: "PUT",
        body: buildCanonicalProfileUpdate(updatedFields),
      });
      if (response.status) {
        setOpen(true);
        const refresh = await dispatch(fetchProfileInfo());
        if (fetchProfileInfo.fulfilled.match(refresh)) {
          dispatch(clearUpdatedFields());
        }
      }
    } catch (error) {
      if (error === "Please authenticate") {
        dispatch(toggleSessionModal(false));
      } else {
        setErrorMessage(error?.message ?? String(error));
        setShowToast(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;
    setShowToast(false);
  };

  if (!localProfile) return null;

  return (
    <>
      <Background extraSpacing>
        <MenteeHeader
          searchTerm={searchTerm}
          handleSearchChange={(event) => setSearchTerm(event.target.value)}
        />
        {loading && <Loader />}
        <Snackbar
          open={showToast}
          autoHideDuration={3000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
            {errorMessage}
          </Alert>
        </Snackbar>

        <Box sx={{ padding: EDIT_PROFILE_PAGE_PADDING }}>
          <Container maxWidth="xl">
            <Grid container spacing={{ lg: 3, sm: 2, xs: 2 }}>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <Mobilesidebar />
                <Sidebar />
              </Grid>
              <Grid item xs={12} sm={8} md={9} lg={9}>
                <Box
                  sx={{
                    padding: { xs: "7px 15px", sm: "20px 30px" },
                    borderRadius: "20px",
                    backgroundColor: "#ffffff",
                    boxShadow: "0px 20px 20px 0px #2A48761A",
                  }}
                >
                  <Box
                    sx={{
                      maxHeight: EDIT_PROFILE_PANEL_MAX_HEIGHT,
                      overflowY: "auto",
                      paddingRight: "10px",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#2A4876",
                        fontWeight: 700,
                        textAlign: "left",
                        fontSize: { xs: "16px", sm: "18px", md: "20px" },
                      }}
                      variant="h6"
                      ref={headingRef}
                      tabIndex={-1}
                    >
                      Edit Profile
                    </Typography>
                    <Grid container spacing={3} sx={{ mt: 0 }}>
                      <Grid item xs={12} md={6}>
                        <V2ProfileBasics
                          key={formKey}
                          localProfile={localProfile}
                        />
                        <V2ProfileDemographics localProfile={localProfile} />
                        <GoalsSection />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <EditProfileRightSection
                          key={formKey}
                          localProfile={localProfile}
                          onDobValidityChange={setHasInvalidDob}
                        />
                        {/* Additional portraits are saved immediately because
                            their endpoint is a whole-set replace, separate from
                            the profile PUT, so Discard leaves them alone. */}
                        <AdditionalPhotosEditor
                          photos={additionalPhotos}
                          onChange={handleAdditionalPhotosChange}
                        />
                        {isMentor && (
                          <V2MentorDetails localProfile={localProfile} />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <Button
                      sx={{
                        padding: "10px 20px",
                        borderRadius: "50px",
                        backgroundColor: "#2A4876",
                        width: { xs: "100%", sm: "60%", md: "35%" },
                        color: "white",
                        textTransform: "none",
                        "&:disabled": { backgroundColor: "#AAB6C8" },
                      }}
                      disabled={disable}
                      onClick={handleSaveChanges}
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        padding: "10px 20px",
                        borderRadius: "50px",
                        borderColor: "#2A4876",
                        width: { xs: "100%", sm: "30%", md: "20%" },
                        color: "#2A4876",
                        textTransform: "none",
                      }}
                      disabled={disableDiscard}
                      onClick={handleDiscard}
                    >
                      Discard
                    </Button>
                  </Box>
                </Box>
                {open && (
                  <SuccessModal
                    open={open}
                    onClose={() => setOpen(false)}
                    title="Profile updated successfully"
                    edit
                  />
                )}
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Background>
    </>
  );
};

export default V2EditProfile;
